import { useState } from "react";
import { Checkbox, CircularProgress, Stack, TablePagination, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Button, FoodsIcon, FoodsTags, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { BulkActionBar, getConsentDisabledActionButtonSx } from "./ConsentAdminShared";
import { ConsentItemRow } from "./ConsentItemRow";

type ConsentItemsTabKind = "documents" | "optIns";

type ConsentItemsTabProps = {
    allVisibleSelected: boolean;
    bulkActionLoading: boolean;
    highlightedItemId: number | null;
    isLoading: boolean;
    isPatchPending: boolean;
    items: ConsentGovernanceItem[];
    kind: ConsentItemsTabKind;
    latestVersionByItemId: Map<number, ConsentGovernanceVersion>;
    page: number;
    paginatedItems: ConsentGovernanceItem[];
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    selectedItemIds: Set<number>;
    selectedItems: ConsentGovernanceItem[];
    totalItems: number;
    onAdd: () => void;
    onBulkDelete: (items: ConsentGovernanceItem[]) => void;
    onBulkSetActive?: (items: ConsentGovernanceItem[], active: boolean) => Promise<void> | void;
    onDelete: (item: ConsentGovernanceItem) => void;
    onEdit: (item: ConsentGovernanceItem) => void;
    onPageChange: (page: number) => void;
    onPreview: (item: ConsentGovernanceItem, version?: ConsentGovernanceVersion) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    onSelect: (itemId: number, selected: boolean) => void;
    onSetVisibleSelection: (items: ConsentGovernanceItem[], selected: boolean) => void;
    onToggleActive: (item: ConsentGovernanceItem, active: boolean) => void;
};

type BulkVisibilityAction = "hide" | "makeVisible";

type ConsentItemsListProps = Pick<
    ConsentItemsTabProps,
    | "highlightedItemId"
    | "isLoading"
    | "isPatchPending"
    | "items"
    | "latestVersionByItemId"
    | "selectedItemIds"
    | "onDelete"
    | "onEdit"
    | "onPreview"
    | "onSelect"
    | "onToggleActive"
> & {
    emptyFallback: string;
    emptyKey: string;
};

const ConsentItemsList = ({
    emptyFallback,
    emptyKey,
    highlightedItemId,
    isLoading,
    isPatchPending,
    items,
    latestVersionByItemId,
    selectedItemIds,
    onDelete,
    onEdit,
    onPreview,
    onSelect,
    onToggleActive,
}: ConsentItemsListProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (isLoading) {
        return (
            <Stack minHeight="200px" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Stack>
        );
    }

    if (items.length === 0) {
        return (
            <WhiteBox padding={theme.spacing(4)} boxShadow={theme.custom.shadows.grey}>
                <Typography variant="body" sx={{ color: theme.custom.grey[2000], textAlign: "center" }}>
                    {t(emptyKey, emptyFallback)}
                </Typography>
            </WhiteBox>
        );
    }

    return (
        <Stack gap={theme.spacing(1)}>
            {items.map((item) => (
                <ConsentItemRow
                    key={item.id}
                    highlighted={highlightedItemId === item.id}
                    initiallyExpanded={highlightedItemId === item.id}
                    isPatchPending={isPatchPending}
                    item={item}
                    latestVersion={latestVersionByItemId.get(item.id)}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onPreview={onPreview}
                    onSelect={onSelect}
                    onToggleActive={onToggleActive}
                    selected={selectedItemIds.has(item.id)}
                />
            ))}
        </Stack>
    );
};

export const ConsentItemsTab = ({
    allVisibleSelected,
    bulkActionLoading,
    highlightedItemId,
    isLoading,
    isPatchPending,
    items,
    kind,
    latestVersionByItemId,
    page,
    paginatedItems,
    rowsPerPage,
    rowsPerPageOptions,
    selectedItemIds,
    selectedItems,
    totalItems,
    onAdd,
    onBulkDelete,
    onBulkSetActive,
    onDelete,
    onEdit,
    onPageChange,
    onPreview,
    onRowsPerPageChange,
    onSelect,
    onSetVisibleSelection,
    onToggleActive,
}: ConsentItemsTabProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isDocumentsTab = kind === "documents";
    const activeOptIns = items.filter((item) => item.active);
    const hiddenOptIns = items.filter((item) => !item.active);
    const [runningBulkVisibilityAction, setRunningBulkVisibilityAction] = useState<BulkVisibilityAction | null>(null);
    const isBulkActionDisabled = bulkActionLoading || runningBulkVisibilityAction !== null;
    const isMakingVisible = runningBulkVisibilityAction === "makeVisible";
    const isHiding = runningBulkVisibilityAction === "hide";
    const bulkButtonSx = {
        justifyContent: "center",
        minHeight: 44,
        minWidth: { xs: "100%", sm: 150 },
        textTransform: "none",
        ...getConsentDisabledActionButtonSx(theme),
    };
    const deleteBulkButtonSx = {
        ...bulkButtonSx,
        ...getConsentDisabledActionButtonSx(theme, theme.palette.error.main),
    };

    const handleBulkSetActive = async (active: boolean) => {
        if (!onBulkSetActive) {
            return;
        }

        setRunningBulkVisibilityAction(active ? "makeVisible" : "hide");

        try {
            await Promise.resolve(onBulkSetActive(selectedItems, active));
        } finally {
            setRunningBulkVisibilityAction(null);
        }
    };

    return (
        <Stack gap={theme.spacing(2)}>
            <Stack
                direction={{ xs: "column", lg: "row" }}
                alignItems={{ xs: "stretch", lg: "center" }}
                justifyContent="space-between"
                gap={theme.spacing(2)}
            >
                <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ minWidth: 0 }}>
                    <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={selectedItems.length > 0 && !allVisibleSelected}
                        disabled={paginatedItems.length === 0}
                        inputProps={{
                            "aria-label": t(
                                isDocumentsTab ?
                                    "settings.consent.selection.select_visible_documents"
                                :   "settings.consent.selection.select_visible_preferences",
                                isDocumentsTab ? "Select visible documents" : "Select visible preferences",
                            ),
                        }}
                        onChange={(event) => onSetVisibleSelection(paginatedItems, event.target.checked)}
                        sx={{ height: 44, width: 44 }}
                    />
                    <Typography variant="body" sx={{ fontWeight: 700 }}>
                        {totalItems}{" "}
                        {isDocumentsTab ?
                            t("settings.consent.documents.count_label", "document(s)")
                        :   t("settings.consent.preferences.count_label", "preference(s)")}
                    </Typography>
                    {!isDocumentsTab && activeOptIns.length > 0 && (
                        <FoodsTags
                            label={`${activeOptIns.length} ${t("settings.consent.preferences.visible_label", "visible")}`}
                            variant="tags-green"
                        />
                    )}
                    {!isDocumentsTab && hiddenOptIns.length > 0 && (
                        <FoodsTags
                            label={`${hiddenOptIns.length} ${t("settings.consent.preferences.hidden_label", "hidden")}`}
                            variant="tags-grey"
                        />
                    )}
                </Stack>
                <Stack direction="row" gap={theme.spacing(1)}>
                    <Button
                        size="small"
                        variant="validate"
                        startIcon={<FoodsIcon icon="add" size={1} />}
                        onClick={onAdd}
                        sx={{
                            justifyContent: "center",
                            minHeight: 44,
                            minWidth: 190,
                            width: { xs: "100%", sm: "auto" },
                            textTransform: "none",
                        }}
                    >
                        {t(
                            isDocumentsTab ?
                                "settings.consent.documents.actions.add_document"
                            :   "settings.consent.preferences.actions.add_preference",
                            isDocumentsTab ? "New purpose" : "New preference",
                        )}
                    </Button>
                </Stack>
            </Stack>
            <BulkActionBar
                count={selectedItems.length}
                loading={isBulkActionDisabled}
                onClear={() => onSetVisibleSelection(paginatedItems, false)}
            >
                {!isDocumentsTab && onBulkSetActive && (
                    <>
                        <Button
                            size="small"
                            variant="validate"
                            disabled={isBulkActionDisabled}
                            startIcon={
                                isMakingVisible ?
                                    <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} />
                                :   <FoodsIcon icon="viewPwd" size={1} />
                            }
                            onClick={() => void handleBulkSetActive(true)}
                            sx={bulkButtonSx}
                        >
                            {isMakingVisible ?
                                `${t("settings.consent.bulk.make_visible", "Make visible")}...`
                            :   t("settings.consent.bulk.make_visible", "Make visible")}
                        </Button>
                        <Button
                            size="small"
                            variant="black-filled"
                            disabled={isBulkActionDisabled}
                            startIcon={
                                isHiding ?
                                    <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} />
                                :   <FoodsIcon icon="hidePwd" size={1} />
                            }
                            onClick={() => void handleBulkSetActive(false)}
                            sx={bulkButtonSx}
                        >
                            {isHiding ?
                                `${t("settings.consent.bulk.hide", "Hide")}...`
                            :   t("settings.consent.bulk.hide", "Hide")}
                        </Button>
                    </>
                )}
                <Button
                    size="small"
                    variant="delete"
                    disabled={isBulkActionDisabled}
                    startIcon={<FoodsIcon icon="delete" size={1} />}
                    onClick={() => onBulkDelete(selectedItems)}
                    sx={deleteBulkButtonSx}
                >
                    {t("common.actions.delete", "Delete")}
                </Button>
            </BulkActionBar>
            <ConsentItemsList
                emptyFallback={isDocumentsTab ? "No contractual document configured" : "No optional opt-in configured"}
                emptyKey={
                    isDocumentsTab ? "settings.consent.documents.empty.title" : "settings.consent.optins.empty.title"
                }
                highlightedItemId={highlightedItemId}
                isLoading={isLoading}
                isPatchPending={isPatchPending}
                items={paginatedItems}
                latestVersionByItemId={latestVersionByItemId}
                selectedItemIds={selectedItemIds}
                onEdit={onEdit}
                onDelete={onDelete}
                onPreview={onPreview}
                onSelect={onSelect}
                onToggleActive={onToggleActive}
            />
            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={(_, nextPage) => {
                    onPageChange(nextPage);
                    onSetVisibleSelection(paginatedItems, false);
                }}
                onRowsPerPageChange={(event) => {
                    onRowsPerPageChange(Number(event.target.value));
                }}
            />
        </Stack>
    );
};
